import { isEqual } from 'lodash';
import { ListFilter, ZoomIn, ZoomOut, Eye, EyeOff, ExternalLink } from 'lucide-react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import Avatar from '@/assets/pngs/Avatar.png';
import IncidentCluster from '@/assets/pngs/incident_cluster.png';
import IncidentPin from '@/assets/pngs/incident_pin.png';
import ResponderCluster from '@/assets/pngs/responder_cluster.png';
import { Button, useToast } from '@/components';
import IncidentClusterTooltip from '@/components/shared/IncidentClusterTooltip/IncidentClusterTooltip';
import { IncidentTooltip } from '@/components/shared/IncidentTooltip/IncidentTooltip';
import { ResponderTooltip } from '@/components/shared/ResponderTooltip/ResponderTooltip';
import { useTheme } from '@/context';
import { searchIconMap, updateGeofenceSource } from './MapCommon';
import { flyToAndShowPin } from './MapCommon';
import {
  constants,
  createCirclePolygon,
  fetchRoute,
  getDistanceInMeters,
  isValidCoordinates,
  mapbox_accessToken,
} from '@/lib';
import { MapFilter } from '@/lib/modules/map';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import IncidentPinSelected from '@/assets/pngs/IncidentPinSelected.png';
import IncidentPinEditing from '@/assets/pngs/IncidentPinEditing.png';
import ResponderSelected from '@/assets/pngs/ResponderSelected.png';

const loadImages = [
  { name: 'incident-cluster-icon', src: IncidentCluster },
  { name: 'responder-cluster-icon', src: ResponderCluster },
  { name: 'incident-icon', src: IncidentPin },
  { name: 'responder-icon', src: Avatar },
  { name: 'responder-selected-icon', src: ResponderSelected },
  { name: 'incident-selected-icon', src: IncidentPinSelected },
  { name: 'incident-editing-icon', src: IncidentPinEditing },
];

const AVAILABLE_PRESENCE_STATUSES = ['online', 'available', 'chat', 'away', 'dnd', 'busy', 'xa'];

const MapView = ({ allContextContent = {}, isPopOut = false }) => {
  mapboxgl.accessToken = mapbox_accessToken;

  const theme = useTheme();
  const responders = useStableResponders();

  // Creating refs to hold live values
  const latestResponderRef = useRef(allContextContent.latestResponder);
  const incidentsRef = useRef(allContextContent.incidents);

  const mapContainer = useRef(null);
  const styleLoadingRef = useRef(false);

  const prevIncidentCache = useRef([]);
  const prevResponderCache = useRef([]);
  const draggableIncidentMarkerRef = useRef(null);

  const [showFilter, setShowFilter] = useState(false);
  const [mapStyle, setMapStyle] = useState('default');

  const [filter, setFilter] = useState({
    incidentEnabled: true,
    incidentFilter: 'all',
    responderEnabled: true,
    responderFilter: 'all',
  });
  const [lastResponderLocations, setLastResponderLocations] = useState({});
  const [lastCenterIncidentLocation, setLastCenterIncidentLocation] = useState([]);
  const prevIncidentIdRef = useRef(null);
  const fitBoundsDoneRef = useRef(false);
  const routeLayersRef = useRef([]);
  const routeDataRef = useRef(new Map());

  const ensureRouteExists = (map, routeId, geometry) => {
    // Guard against style not being loaded
    if (!allContextContent.isMapLoaded) return;
    if (!map.getSource(routeId)) {
      map.addSource(routeId, {
        type: 'geojson',
        data: { type: 'Feature', geometry },
      });
      map.addLayer(
        {
          id: routeId,
          type: 'line',
          source: routeId,
          layout: { 'line-join': 'round', 'line-cap': 'round' },
          paint: {
            'line-color': '#3b82f6',
            'line-width': 4,
            'line-opacity': 0.8,
          },
        },
        'incident-clusters'
      );
      routeLayersRef.current.push(routeId);
    } else {
      map.getSource(routeId).setData({ type: 'Feature', geometry });
    }
  };

  const clearRoutes = map => {
    routeLayersRef.current.forEach(id => {
      if (map.getLayer(id)) map.removeLayer(id);
      if (map.getSource(id)) map.removeSource(id);
    });
    routeLayersRef.current = [];

    // Remove the incident geofence circle
    if (map.getLayer('incident-geofence-layer')) map.removeLayer('incident-geofence-layer');
    if (map.getSource('incident-geofence')) map.removeSource('incident-geofence');
  };

  useEffect(() => {
    // updating live values for tooltip
    latestResponderRef.current = allContextContent.latestResponder;
  }, [allContextContent.latestResponder]);

  useEffect(() => {
    incidentsRef.current = allContextContent.incidents;
  }, [allContextContent.incidents]);

  const drawRoutes = async (responders, centerIncident, map, centerLng, centerLat) => {
    const incidentCoords = [centerLng || centerIncident?.geoloc?.long, centerLat || centerIncident?.geoloc?.lat];

    const centerChanged =
      incidentCoords[0] != lastCenterIncidentLocation[0] || incidentCoords[1] != lastCenterIncidentLocation[1];
    if (centerChanged) {
      setLastCenterIncidentLocation(incidentCoords);
    }

    if (!isValidCoordinates(incidentCoords)) {
      return;
    }

    const bounds = new mapboxgl.LngLatBounds().extend(incidentCoords);

    let hasValidResponder = false;
    let hasPlottedRoute = false;

    for (const [index, responder] of responders.entries()) {
      const responderCoords = [responder.lastKnownLocation.lon, responder.lastKnownLocation.lat];

      if (!isValidCoordinates(responderCoords)) continue;
      hasValidResponder = true;

      const lastCoords = lastResponderLocations[responder.id];
      const distanceMoved = lastCoords
        ? getDistanceInMeters(lastCoords[1], lastCoords[0], responderCoords[1], responderCoords[0])
        : 0;
      const hasMoved = !lastCoords || distanceMoved > 5;
      const distance = getDistanceInMeters(
        incidentCoords[1],
        incidentCoords[0],
        responder.lastKnownLocation.lat,
        responder.lastKnownLocation.lon
      );
      if (distance <= 200) {
        bounds.extend(responderCoords);
        continue;
      }
      if (hasMoved || centerChanged) {
        //  Always clear previous routes, geofence, and markers first
        routeDataRef.current.clear();

        if (map.getLayer('incident-geofence-layer'))
          // Remove geofence if it exists
          map.removeLayer('incident-geofence-layer');
        if (map.getSource('incident-geofence')) map.removeSource('incident-geofence');

        if (map.getLayer('selected-incident-layer'))
          // Remove selected incident highlight
          map.removeLayer('selected-incident-layer');
        if (map.getSource('selected-incident')) map.removeSource('selected-incident');
        if (allContextContent.selectedResponder?.id == responder?.id) {
          allContextContent.setSelectedResponder(responder);
        }
        const routeGeometry = await fetchRoute(responderCoords, incidentCoords, routeDataRef);

        if (routeGeometry) {
          ensureRouteExists(map, `route-${responder.id}`, routeGeometry);
          bounds.extend(responderCoords);
          hasPlottedRoute = true;
        }

        // Update cache
        // lastResponderLocations[responder.id] = responderCoords;
        setLastResponderLocations(prev => ({
          ...prev,
          [responder.id]: responderCoords,
        }));
      } else {
        bounds.extend(responderCoords);

        if (map.getLayer(`route-${responder.id}`)) {
          hasPlottedRoute = true;
        }
      }
    }

    if (!hasValidResponder) {
      return;
    }

    if (allContextContent.editingIncidentAndCandidates === null) {
      //only zoom map to fit bound when it is not editing/creating incident
      if (!fitBoundsDoneRef.current && !bounds.isEmpty()) {
        map.fitBounds(bounds, { padding: 100, duration: 1000 });
        fitBoundsDoneRef.current = true;
      }
    }

    // Only create geofence if at least one route was plotted
    // Guard against style not being loaded (could have changed during async fetchRoute)
    if (!allContextContent.isMapLoaded) return;

    if (hasPlottedRoute) {
      const circle = createCirclePolygon(incidentCoords, 0.2, { units: 'kilometers' });
      if (!map.getSource('incident-geofence')) {
        map.addSource('incident-geofence', { type: 'geojson', data: circle });
        map.addLayer(
          {
            id: 'incident-geofence-layer',
            type: 'fill',
            source: 'incident-geofence',
            paint: { 'fill-color': '#3b82f6', 'fill-opacity': 0.2 },
          },
          'incident-clusters'
        );
      } else {
        map.getSource('incident-geofence').setData(circle);
      }
    } else {
      // Remove geofence if no routes are plotted
      if (map.getLayer('incident-geofence-layer')) {
        map.removeLayer('incident-geofence-layer');
      }
      if (map.getSource('incident-geofence')) {
        map.removeSource('incident-geofence');
      }
    }
  };

  // MAP INITIALIZATION
  useEffect(() => {
    if (!mapContainer.current) return;

    // Initialize map only once
    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: `mapbox://styles/mapbox/${theme.theme}-v10`,
      center: constants.DEFAULT_COORD,
      zoom: 8,
      maxBounds: [
        [-180, -85],
        [180, 85],
      ],
      wrap: false,
      pitch: 0,
      maxPitch: 0,
      minPitch: 0,
      pitchWithRotate: false,
      dragRotate: false,
      touchZoomRotate: false,
    });
    allContextContent.mapRef.current = map;
    const mapDraw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        //polygon: true,
        //trash: true
      },
      defaultMode: 'simple_select',
    });
    allContextContent.mapDrawRef.current = mapDraw;
    allContextContent.mapRef.current.addControl(mapDraw, 'top-left');
    map.on('load', async () => {
      try {
        await setupMapLayers(map);
        allContextContent.setIsMapLoaded(true);
      } catch (error) {
        console.error('load map:', error);
      }
    });

    const observer = new ResizeObserver(() => {
      if (map) map.resize();
    });

    observer.observe(mapContainer.current);

    return () => {
      observer.disconnect();
      map.remove();
      allContextContent.mapRef.current = null;
    };
  }, []);

  // DRAW A ROUTE FROM RESPONDERS TO INCIDENT
  useEffect(() => {
    const map = allContextContent.mapRef.current;
    if (allContextContent.selectedIncident?.id !== prevIncidentIdRef.current) {
      setLastResponderLocations({});
      if (allContextContent.editingIncidentAndCandidates === null) clearRoutes(map);
      prevIncidentIdRef.current = allContextContent.selectedIncident?.id || null;
      fitBoundsDoneRef.current = false;
    }

    var responsers = [];
    if (allContextContent.selectedIncident && allContextContent.users) {
      const assigneeIds = allContextContent.selectedIncident.assignees?.map(a => a.id) || [];
      responsers = allContextContent.users.filter(u => assigneeIds.includes(u.id.split('@')[0]) && u.lastKnownLocation);
    }

    if (!allContextContent.selectedIncident || allContextContent.selectedIncident?.status === 'resolved') {
      if (allContextContent.editingIncidentAndCandidates === null) clearRoutes(map);
      return;
    }
    if (!map) return;
    if (map.isStyleLoaded()) {
      drawRoutes(responsers, allContextContent.selectedIncident, map);
    }
  }, [allContextContent.selectedIncident, allContextContent.users, allContextContent.isMapLoaded]);

  useEffect(() => {
    const map = allContextContent.mapRef.current;

    // Dynamically update the filter on the existing layer
    if (allContextContent.isMapLoaded) {
      if (allContextContent.editingIncidentAndCandidates?.incident?.id) {
        // Hide the specific incident being edited
        map.setFilter('incident-unclustered-point', [
          'all',
          ['!', ['has', 'point_count']],
          ['!=', ['get', 'id'], allContextContent.editingIncidentAndCandidates.incident.id],
        ]);
      } else {
        map.setFilter('incident-unclustered-point', ['!', ['has', 'point_count']]);
      }
    }

    if (!allContextContent.editingIncidentAndCandidates) {
      if (!allContextContent.selectedIncident?.id) clearRoutes(map);
      return;
    }

    var responsers = [];
    const assigneeIds = allContextContent.editingIncidentAndCandidates.candidates?.map(a => a.id.split('@')[0]) || [];
    responsers = allContextContent.users.filter(u => assigneeIds.includes(u.id.split('@')[0]) && u.lastKnownLocation);

    const existingRouteIds = new Set(
      (map.getStyle()?.layers || []).filter(layer => layer.id.startsWith('route-')).map(layer => layer.id)
    );
    existingRouteIds.forEach(layerId => {
      const checkID = layerId.replace('route-', '');

      const stillExists = responsers?.some(r => r.id.toString() === checkID);
      if (!stillExists) {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(layerId)) map.removeSource(layerId);
      }
    });
    drawRoutes(
      responsers,
      allContextContent.editingIncidentAndCandidates.incident,
      map,
      allContextContent.createIncidentMarker?.coord?.[0],
      allContextContent.createIncidentMarker?.coord?.[1]
    );
  }, [allContextContent.editingIncidentAndCandidates, allContextContent.users, allContextContent.createIncidentMarker]);

  // Helper function to load all images and return a promise
  const loadAllImages = map => {
    return Promise.all(
      loadImages.map(({ name, src }) => {
        return new Promise((resolve, reject) => {
          if (map.hasImage(name)) {
            resolve();
            return;
          }

          map.loadImage(src, (err, img) => {
            if (err) {
              console.error(`Error loading image ${name}:`, err);
              reject(err);
            } else {
              map.addImage(name, img);
              resolve();
            }
          });
        });
      })
    );
  };

  // Helper function to get filtered data
  const getFilteredData = () => {
    let filteredIncidents = allContextContent.incidents;
    let filterResponders = responders;

    if (filter.incidentEnabled) {
      if (filter.incidentFilter === 'active') {
        filteredIncidents = allContextContent.incidents.filter(
          incident => !['resolved', 'closed'].includes(incident.status?.toLowerCase())
        );
      } else if (filter.incidentFilter === 'completed') {
        filteredIncidents = allContextContent.incidents.filter(incident =>
          ['resolved', 'closed'].includes(incident.status?.toLowerCase())
        );
      } else {
        filteredIncidents = allContextContent.incidents;
      }
    } else {
      filteredIncidents = [];
    }

    if (filter.responderEnabled) {
      if (filter.responderFilter === constants.MAP_STATUSES.AVAILABLE) {
        filterResponders = responders.filter(responder =>
          AVAILABLE_PRESENCE_STATUSES.includes(responder.presence?.toLowerCase())
        );
      } else {
        filterResponders = responders;
      }
    } else {
      filterResponders = [];
    }

    return { filteredIncidents, filterResponders };
  };

  // Helper function to setup map layers
  const setupMapLayers = async map => {
    allContextContent.setIsMapLoaded(false);
    // Load all images first and wait for them to complete
    try {
      await loadAllImages(map);
    } catch (err) {
      console.error('Error loading images:', err);
    }

    // Get filtered data based on current filter state
    const { filteredIncidents, filterResponders } = getFilteredData();

    const incidentGeoJSON = {
      type: 'FeatureCollection',
      features: filteredIncidents.map(i => ({
        type: 'Feature',
        properties: {
          id: i.id,
          type: i.type,
          title: i.title,
          reportedDateTime: i.creationTimestamp,
          severity: i.severity,
          status: i.status,
          assignees: i.assignees,
        },
        geometry: { type: 'Point', coordinates: [i?.geoloc?.long, i?.geoloc?.lat] },
      })),
    };

    const responderGeoJSON = {
      type: 'FeatureCollection',
      features: filterResponders
        .filter(r => r?.lastKnownLocation?.lon != null && r?.lastKnownLocation?.lat != null)
        .map(r => ({
          type: 'Feature',
          properties: { id: r.id, displayName: r.displayName },
          geometry: { type: 'Point', coordinates: [r?.lastKnownLocation?.lon, r?.lastKnownLocation?.lat] },
        })),
    };

    // Add sources only if they don't exist
    if (!map.getSource('incidents')) {
      map.addSource('incidents', {
        type: 'geojson',
        data: incidentGeoJSON,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });
    } else {
      // Update existing source
      map.getSource('incidents').setData(incidentGeoJSON);
    }

    if (!map.getSource('responders')) {
      map.addSource('responders', {
        type: 'geojson',
        data: responderGeoJSON,
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });
    } else {
      // Update existing source
      map.getSource('responders').setData(responderGeoJSON);
    }

    if (!map.getSource('geofence')) {
      map.addSource('geofence', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      });
    }

    map.addLayer({
      id: 'geofence',
      type: 'line',
      source: 'geofence',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': searchIconMap['geofence'].color,
        'line-width': 2,
        'line-opacity': 0.8,
      },
    });

    // Re-apply any cached geofence data that was set before the source existed
    // (e.g. from checked geofences resent when popup map opened)
    if (allContextContent?.mapLinesRef?.current) {
      updateGeofenceSource(map, allContextContent.mapLinesRef.current);
    }

    // Add layers and handlers
    addClusterAndPointLayers(map);
    addClusterClickHandlers(map);
    addMarkerInteractionHandlers(map);
  };

  useEffect(() => {
    if (!allContextContent.mapRef.current) return;
    const map = allContextContent.mapRef.current;

    const performUpdateRoutes = () => {
      // Double-check style is loaded before proceeding
      if (!map.isStyleLoaded()) return;
      updateRoutes();
    };

    if (!map.isStyleLoaded()) {
      map.once('style.load', performUpdateRoutes);
    } else {
      updateRoutes();
    }

    function updateRoutes() {
      const existingRouteIds = new Set(
        (map.getStyle()?.layers || []).filter(layer => layer.id.startsWith('hoverroute-')).map(layer => layer.id)
      );

      existingRouteIds.forEach(layerId => {
        const responderId = layerId.replace('hoverroute-', '');

        const stillExists = allContextContent.respondersForCreateIncident?.some(r => r.id.toString() === responderId);
        if (!stillExists) {
          if (map.getLayer(layerId)) map.removeLayer(layerId);
          if (map.getSource(layerId)) map.removeSource(layerId);
        }
      });

      allContextContent.respondersForCreateIncident.forEach(responder => {
        if (!responder.route) return;

        const sourceId = `hoverroute-${responder.id}`;
        const layerId = `hoverroute-${responder.id}`;

        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(sourceId)) map.removeSource(sourceId);

        map.addSource(sourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: responder.route,
          },
        });

        map.addLayer(
          {
            id: layerId,
            type: 'line',
            source: sourceId,
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
            paint: {
              'line-color': theme?.theme == 'dark' ? '#ffffff' : '#000000',
              'line-width': 3,
              'line-opacity': responder?.isSelected ? 1 : responder?.isHover ? 0.4 : 0,
              'line-dasharray': [2, 2],
            },
          },
          'incident-clusters'
        );
      });
    }
  }, [allContextContent.respondersForCreateIncident]);

  // UPDATE EFFECT (INCIDENTS, RESPONDERS, HIGHLIGHTS) with filtering
  useEffect(() => {
    const map = allContextContent.mapRef.current;
    if (!map || !allContextContent.isMapLoaded) return;

    let filteredIncidents = [];
    if (Array.isArray(allContextContent.incidents)) {
      if (filter.incidentEnabled) {
        if (filter.incidentFilter === 'active') {
          filteredIncidents = allContextContent.incidents.filter(
            incident => !['resolved', 'closed'].includes(incident.status?.toLowerCase())
          );
        } else if (filter.incidentFilter === 'completed') {
          filteredIncidents = allContextContent.incidents.filter(incident =>
            ['resolved', 'closed'].includes(incident.status?.toLowerCase())
          );
        } else {
          filteredIncidents = allContextContent.incidents;
        }
      }
    }

    // Responder Filtering
    let filteredResponders = [];
    if (Array.isArray(responders)) {
      if (filter.responderEnabled) {
        if (filter.responderFilter === constants.MAP_STATUSES.AVAILABLE) {
          filteredResponders = responders.filter(responder =>
            AVAILABLE_PRESENCE_STATUSES.includes(responder.presence?.toLowerCase())
          );
        } else {
          filteredResponders = responders;
        }
      }
    }

    // Update Incident Layer
    const incidentFeatures = filteredIncidents.map(i => ({
      type: 'Feature',
      properties: {
        id: i.id,
        type: i.type,
        title: i.title,
        reportedDateTime: i.creationTimestamp,
        severity: i.severity,
        status: i.status,
        assignees: i.assignees,
      },
      geometry: { type: 'Point', coordinates: [i?.geoloc?.long, i?.geoloc?.lat] },
    }));

    map.getSource('incidents')?.setData({
      type: 'FeatureCollection',
      features: incidentFeatures,
    });
    prevIncidentCache.current = filteredIncidents;

    // Update Responder Layer
    const responderFeatures = filteredResponders
      .filter(r => r?.lastKnownLocation?.lat != null && r?.lastKnownLocation?.lon != null)
      .map(r => ({
        type: 'Feature',
        properties: { id: r?.id, displayName: r?.displayName },
        geometry: {
          type: 'Point',
          coordinates: [r?.lastKnownLocation?.lon, r.lastKnownLocation?.lat],
        },
      }));

    map.getSource('responders')?.setData({
      type: 'FeatureCollection',
      features: responderFeatures,
    });
    prevResponderCache.current = filteredResponders;
  }, [allContextContent.incidents, responders, filter, allContextContent.isMapLoaded]);

  useEffect(() => {
    const map = allContextContent.mapRef.current;
    if (allContextContent.selectedIncident?.geoloc) {
      // if (!map || !map.isStyleLoaded()) return;

      // Selected incident
      updateHighlight(
        map,
        'incident',
        [allContextContent.selectedIncident?.geoloc?.long, allContextContent.selectedIncident?.geoloc?.lat],
        'incident-selected-icon'
      );
    } else {
      // If unselected → show default marker
      updateHighlight(map, 'incident', null, 'incident-icon');
    }
    if (allContextContent.selectedResponder?.lastKnownLocation) {
      updateHighlight(
        map,
        'responder',
        [
          allContextContent.selectedResponder?.lastKnownLocation?.lon,
          allContextContent.selectedResponder?.lastKnownLocation.lat,
        ],
        'responder-selected-icon'
      );
    } else {
      updateHighlight(map, 'responder', null, 'responder-icon');
    }
  }, [allContextContent.selectedIncident, allContextContent.selectedResponder, allContextContent.isMapLoaded]);

  // HELPER FUNCTIONS
  const addClusterAndPointLayers = map => {
    // Remove existing layers if they exist
    const layerIds = [
      'incident-clusters',
      'incident-unclustered-point',
      'responder-clusters',
      'responder-unclustered-point',
    ];

    layerIds.forEach(id => {
      if (map.getLayer(id)) {
        map.removeLayer(id);
      }
    });

    // Incidents
    map.addLayer({
      id: 'incident-clusters',
      type: 'symbol',
      source: 'incidents',
      filter: ['has', 'point_count'],
      layout: {
        'icon-image': 'incident-cluster-icon',
        'icon-size': 1.0,
        'icon-allow-overlap': true,
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
        'text-size': 14,
        'text-offset': [0, -0.6],
        'text-anchor': 'top',
        'text-allow-overlap': true,
        visibility: filter.incidentEnabled ? 'visible' : 'none',
      },
    });

    map.addLayer({
      id: 'incident-unclustered-point',
      type: 'symbol',
      source: 'incidents',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': 'incident-icon',
        'icon-size': 0.8,
        'icon-allow-overlap': true,
        visibility: filter.incidentEnabled ? 'visible' : 'none',
      },
    });

    // Responders
    map.addLayer({
      id: 'responder-clusters',
      type: 'symbol',
      source: 'responders',
      filter: ['has', 'point_count'],
      layout: {
        'icon-image': 'responder-cluster-icon',
        'icon-size': 1.0,
        'icon-allow-overlap': true,
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
        'text-size': 14,
        'text-offset': [0, -0.6],
        'text-anchor': 'top',
        'text-allow-overlap': true,
        visibility: filter.responderEnabled ? 'visible' : 'none',
      },
    });

    map.addLayer({
      id: 'responder-unclustered-point',
      type: 'symbol',
      source: 'responders',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': 'responder-icon',
        'icon-size': 1.0,
        'icon-allow-overlap': true,
        visibility: filter.responderEnabled ? 'visible' : 'none',
      },
    });
  };

  const addClusterClickHandlers = map => {
    map.on('click', 'incident-clusters', e => {
      const features = map.queryRenderedFeatures(e.point, { layers: ['incident-clusters'] });
      const clusterId = features[0].properties.cluster_id;
      map.getSource('incidents').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (!err) map.easeTo({ center: features[0].geometry.coordinates, zoom });
      });
    });

    map.on('click', 'responder-clusters', e => {
      const features = map.queryRenderedFeatures(e.point, { layers: ['responder-clusters'] });
      const clusterId = features[0].properties.cluster_id;
      map.getSource('responders').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (!err) map.easeTo({ center: features[0].geometry.coordinates, zoom });
      });
    });

    ['incident-clusters', 'responder-clusters'].forEach(layer => {
      map.on('mouseenter', layer, () => (map.getCanvas().style.cursor = 'pointer'));
      map.on('mouseleave', layer, () => (map.getCanvas().style.cursor = ''));
    });
  };

  const updateHighlight = (map, id, coords, iconImage) => {
    if (!map) return;

    const sourceId = `${id}-highlight`;

    const performUpdate = () => {
      // Always clear old layer/source first
      if (map.getLayer(sourceId)) map.removeLayer(sourceId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);

      // If no valid coords (unselected), stop here
      if (!coords || coords.some(c => typeof c !== 'number' || isNaN(c))) return;

      // Add new source for highlight marker
      map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [{ type: 'Feature', geometry: { type: 'Point', coordinates: coords } }],
        },
      });

      // Add marker (bordered or default based on iconImage)
      map.addLayer({
        id: sourceId,
        type: 'symbol',
        source: sourceId,
        layout: {
          'icon-image': iconImage,
          'icon-size': 0.6,
          'icon-allow-overlap': true,
        },
      });

      flyToAndShowPin(coords[1], coords[0], 14, allContextContent.mapRef.current);
    };

    // Wait for style to be loaded before adding sources/layers
    if (map.isStyleLoaded()) {
      performUpdate();
    } else {
      map.once('style.load', performUpdate);
    }
  };

  function useStableResponders() {
    const prevUsersRef = useRef([]);
    const prevRespondersRef = useRef([]);
    // console.log('calling useSatbleResponder');

    const responders = useMemo(() => {
      // Compute new responders
      const nextResponders = allContextContent.users.filter(u => u.permission === 'MobileUnit');

      // Compare with previous responders deeply
      if (isEqual(nextResponders, prevRespondersRef.current)) {
        // No change → return previous reference (prevents flicker)
        return prevRespondersRef.current;
      }

      // Update refs and return new responders
      prevUsersRef.current = [...allContextContent.users];
      prevRespondersRef.current = nextResponders;
      return nextResponders;
    }, [allContextContent.users]);

    return responders;
  }

  const addMarkerInteractionHandlers = map => {
    let activePopup = null;
    let activeLayer = null;

    function showPopup(layerId, feature, customHTML) {
      const coordinates = feature.geometry.coordinates.slice();

      if (activePopup) activePopup.remove();

      activePopup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 25,
        className: layerId === 'incident-clusters' ? 'incident-cluster-popup' : '',
      })
        .setLngLat(coordinates)
        .setHTML(customHTML)
        .addTo(map);

      activeLayer = layerId;
    }

    function removePopup() {
      if (activePopup) {
        activePopup.remove();
        activePopup = null;
        activeLayer = null;
        map.getCanvas().style.cursor = '';
      }
    }

    // incident points
    map.on('mouseenter', 'incident-unclustered-point', e => {
      map.getCanvas().style.cursor = 'pointer';
      const feature = e.features[0];
      if (!feature) return;

      const { properties } = feature;
      const tooltipHTML = ReactDOMServer.renderToString(
        <IncidentTooltip
          title={properties.title}
          type={properties.type}
          status={properties.status}
          assignees={properties.assignees}
          creationTimestamp={properties.reportedDateTime}
          severity={properties.severity}
          latestResponder={latestResponderRef.current}
          incidentAssigneeStatusList={allContextContent.incidentAssigneeStatusList}
          users={allContextContent.users}
        />
      );

      showPopup('incident-unclustered-point', feature, tooltipHTML);
    });

    map.on('click', 'incident-unclustered-point', e => {
      const feature = e.features[0];
      if (!feature) return;
      const selected = incidentsRef.current.find(i => i.id === feature.properties.id);
      if (selected) allContextContent.focusIncident(selected);

      // remove tooltip on click
      removePopup();
    });

    // responder points
    map.on('mouseenter', 'responder-unclustered-point', e => {
      map.getCanvas().style.cursor = 'pointer';
      const feature = e.features[0];
      if (!feature) return;

      const tooltipHTML = ReactDOMServer.renderToString(
        <ResponderTooltip
          responder={feature.properties}
          latestResponder={latestResponderRef.current}
          incidentAssigneeStatusList={allContextContent.incidentAssigneeStatusList}
        />
      );
      showPopup('responder-unclustered-point', feature, tooltipHTML);
    });

    map.on('click', 'responder-unclustered-point', e => {
      const feature = e.features[0];
      if (!feature) return;
      const selected = responders.find(r => r.id === feature.properties.id);
      if (selected) allContextContent.focusResponder(selected);

      // remove tooltip on click
      removePopup();
    });

    // incident clusters
    map.on('mouseenter', 'incident-clusters', e => {
      map.getCanvas().style.cursor = 'pointer';
      const feature = e.features[0];
      if (!feature) return;

      const clusterId = feature.properties.cluster_id;

      map.getSource('incidents').getClusterLeaves(clusterId, 5, 0, (err, leaves) => {
        if (err) return console.error(err);

        const total = feature.properties.point_count;
        const titles = leaves.map(l => l.properties.title);

        const tooltipHTML = ReactDOMServer.renderToString(<IncidentClusterTooltip count={total} titles={titles} />);

        showPopup('incident-clusters', feature, tooltipHTML);
      });
    });

    map.on('click', 'incident-clusters', e => {
      const feature = e.features[0];
      if (!feature) return;

      const clusterId = feature.properties.cluster_id;

      // zoom into cluster on click
      map.getSource('incidents').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return console.error(err);
        map.easeTo({ center: feature.geometry.coordinates, zoom });
      });

      removePopup();
    });

    // global leave detection
    map.on('mousemove', e => {
      if (!activeLayer) return;

      const features = map.queryRenderedFeatures(e.point, { layers: [activeLayer] });
      if (!features.length) removePopup();
    });

    // Remove popup on drag/zoom
    map.on('dragstart', removePopup);
    map.on('zoomstart', removePopup);
  };

  useEffect(() => {
    const map = allContextContent.mapRef.current;
    if (!map) return;

    // Clear any existing draggable marker
    if (draggableIncidentMarkerRef.current) {
      draggableIncidentMarkerRef.current.remove();
      draggableIncidentMarkerRef.current = null;
    }

    if (!allContextContent.createIncidentMarker) {
      // Remove highlight if marker creation is cancelled
      updateHighlight(map, 'incident', null);
      return;
    }

    const { coord, isDraggable } = allContextContent.createIncidentMarker;

    if (isDraggable) {
      // Create a real draggable marker
      const el = document.createElement('div');
      el.style.backgroundImage = `url(${IncidentPinEditing})`;
      el.style.backgroundSize = 'contain';
      el.style.backgroundRepeat = 'no-repeat';
      el.style.width = '40px';
      el.style.height = '40px';
      el.style.cursor = 'grab';
      el.style.transform = 'translate(-50%, -100%)'; // position the tip correctly
      const marker = new mapboxgl.Marker({ element: el, draggable: true }).setLngLat(coord).addTo(map);

      // When drag ends, update the coordinate in state
      marker.on('dragend', () => {
        const lngLat = marker.getLngLat();
        const newCoord = [lngLat.lng, lngLat.lat];
        allContextContent.updateDraggableIncidentMarker(newCoord);
      });

      draggableIncidentMarkerRef.current = marker;

      flyToAndShowPin(coord[1], coord[0], null, allContextContent.mapRef.current);
    } else {
      // Use your existing highlight approach for static markers
      updateHighlight(map, 'incident', coord, 'incident-selected-icon', {
        'circle-radius': 15,
        'circle-color': '#2563EB',
        'circle-opacity': 0.4,
        'circle-stroke-color': '#2563EB',
        'circle-stroke-width': 1,
      });
    }
  }, [allContextContent.createIncidentMarker]);

  useEffect(() => {
    //this useEffect only happen in the main app
    if (!allContextContent.editingMarkerFromPopMap) return;
    if (allContextContent.editingMarkerFromPopMap?.category === 'editingIncident')
      allContextContent.updateDraggableIncidentMarker(allContextContent.editingMarkerFromPopMap.coord);
  }, [allContextContent.editingMarkerFromPopMap]);

  useEffect(() => {
    if (!allContextContent.mapRef.current) return;

    const map = allContextContent.mapRef.current;

    // If current view is satellite, just re-setup layers manually
    if (mapStyle === 'satellite') {
      setupMapLayers(map);
      return;
    }

    // Otherwise change style as usual
    updateMapStyle(mapStyle, theme.theme);
  }, [theme.theme]);

  const updateMapStyle = (styleType = mapStyle, currentTheme = theme.theme) => {
    if (!allContextContent.mapRef.current) return;
    const map = allContextContent.mapRef.current;

    // Avoid triggering multiple style loads
    if (styleLoadingRef.current || !map.isStyleLoaded()) return;

    const newStyle =
      styleType === 'satellite' ? 'mapbox://styles/mapbox/satellite-v9' : `mapbox://styles/mapbox/${currentTheme}-v10`;

    // Skip reload if same style is already applied
    const currentSprite = map.getStyle()?.sprite;
    if (currentSprite && currentSprite.includes(newStyle)) return;

    styleLoadingRef.current = true;
    map.setStyle(newStyle);

    map.once('style.load', async () => {
      styleLoadingRef.current = false;
      try {
        await setupMapLayers(map);
        allContextContent.setIsMapLoaded(true);
        setLastResponderLocations({});
      } catch (error) {
        console.error('Failed to re-initialize map layers after style change:', error);
      }
    });
  };

  function popMap() {
    if (!allContextContent.popMapWindowRef.current || allContextContent.popMapWindowRef.current.closed) {
      const width = window.innerWidth;
      const height = window.innerHeight;
      allContextContent.popMapWindowRef.current = window.open(
        '/mapwindow',
        'Map Window',
        `width=${width},height=${height}`
      );
    } else {
      allContextContent.popMapWindowRef.current.focus();
    }
  }

  return (
    <div className="w-full h-full relative overflow-hidden items-center justify-center flex">
      <div
        ref={mapContainer}
        className="absolute inset-0 w-full h-full"
        style={{
          display: !isPopOut && allContextContent.isMapHidden ? 'none' : 'block', // only control by display but not unmount so map display is smooth from hide to show
        }}
      />

      {!isPopOut && allContextContent.isMapHidden && <p>Map view is hidden</p>}

      {/* Map Controls */}
      {!isPopOut && (
        <div className="absolute right-5 top-2 z-10 px-2 h-11 rounded-md bg shadow-md">
          <div className="flex justify-around items-center w-full h-full">
            <Button
              variant="ghost"
              size="ghostIcon"
              onClick={() => {
                allContextContent.setIsMapHidden(!allContextContent.isMapHidden);
              }}
              title="Map Visiability"
            >
              {allContextContent.isMapHidden ? <EyeOff /> : <Eye />}
            </Button>
            <Button variant="ghost" size="ghostIcon" onClick={() => popMap()} title="Pop out">
              <ExternalLink />
            </Button>
            {!allContextContent.isMapHidden && (
              <Button variant="ghost" size="ghostIcon" onClick={() => setShowFilter(!showFilter)} title="Filter">
                <ListFilter />
              </Button>
            )}
          </div>

          {showFilter && (
            <div className="absolute top-full right-0 mt-1 z-20">
              <MapFilter
                handleIncidentEnable={enabled => setFilter(prev => ({ ...prev, incidentEnabled: enabled }))}
                handleResponderEnable={enabled => setFilter(prev => ({ ...prev, responderEnabled: enabled }))}
                responderEnabled={filter.responderEnabled}
                incidentEnabled={filter.incidentEnabled}
                filterResponder={status => setFilter(prev => ({ ...prev, responderFilter: status }))}
                filterIncident={status => setFilter(prev => ({ ...prev, incidentFilter: status }))}
                onMapStyleChange={styleType => {
                  setMapStyle(styleType);
                  updateMapStyle(styleType, theme.theme);
                  allContextContent.unFocusResponder();
                  allContextContent.unFocusIncident();
                }}
                isSatellite={mapStyle === 'satellite'}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MapView;
